import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Tile,
  Button,
  InlineNotification,
  Loading,
  Tag,
} from '@carbon/react';
import { Currency, CheckmarkFilled } from '@carbon/icons-react';
import { getDocumentByToken, createStripeCheckout, updateDocumentStatus } from '@/lib/documentsApi';

const InvoicePayPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    getDocumentByToken(token)
      .then(async (d) => {
        if (searchParams.get('paid') === '1' && d.status !== 'paid') {
          try {
            const updated = await updateDocumentStatus(token, 'paid');
            setDoc(updated);
            return;
          } catch {
            setDoc({ ...d, status: 'paid' });
            return;
          }
        }
        setDoc(d);
      })
      .catch(() => setError('Invoice not found.'))
      .finally(() => setLoading(false));
  }, [token, searchParams]);

  const invoice = doc?.data || {};
  const symbols = { AUD: '$', USD: '$', EUR: '€', GBP: '£', NZD: '$' };
  const sym = symbols[invoice.currency] || '$';
  const format = (n) => `${sym}${(n || 0).toFixed(2)}`;

  const handlePay = async () => {
    setPaying(true);
    setError('');
    try {
      const { url } = await createStripeCheckout(token);
      if (url) {
        window.location.href = url;
      } else {
        setError('Stripe checkout unavailable. Set STRIPE_SECRET_KEY in Netlify.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Payment unavailable');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="public-page loading-screen">
        <Loading withOverlay={false} description="Loading invoice..." />
      </div>
    );
  }

  if (error && !doc) {
    return (
      <div className="public-page">
        <Tile>
          <InlineNotification kind="error" title="Error" subtitle={error} hideCloseButton />
          <Link to="/">← Back to SenseStack</Link>
        </Tile>
      </div>
    );
  }

  const isPaid = doc?.status === 'paid';

  return (
    <div className="public-page">
      <div className="public-header">
        <Link to="/" className="public-brand">SenseStack</Link>
        <Tag type={isPaid ? 'green' : 'purple'}>{isPaid ? 'Paid' : doc?.status || 'Unpaid'}</Tag>
      </div>

      <Tile className="public-document-tile invoice-view">
        <h1>Invoice {invoice.invoiceNumber}</h1>
        <div className="invoice-view-grid">
          <div>
            <h4>From</h4>
            <p>{invoice.companyName}</p>
            <p>{invoice.companyEmail}</p>
          </div>
          <div>
            <h4>Bill To</h4>
            <p>{invoice.clientName}</p>
            <p>{invoice.clientEmail}</p>
          </div>
          <div>
            <h4>Details</h4>
            <p>Date: {invoice.date}</p>
            <p>Due: {invoice.dueDate || 'On receipt'}</p>
          </div>
        </div>

        <table className="invoice-view-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, i) => (
              <tr key={i}>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{format(item.rate)}</td>
                <td>{format(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="invoice-view-totals">
          <div><span>Subtotal</span><span>{format(invoice.subtotal)}</span></div>
          <div><span>Tax ({invoice.taxRate}%)</span><span>{format(invoice.taxAmount)}</span></div>
          <div className="grand"><span>Total</span><span>{format(invoice.total)}</span></div>
        </div>

        {invoice.notes && (
          <div className="invoice-view-notes">
            <h4>Notes</h4>
            <p>{invoice.notes}</p>
          </div>
        )}

        {error && (
          <InlineNotification kind="error" title="Payment error" subtitle={error} hideCloseButton />
        )}

        {isPaid ? (
          <div className="payment-success">
            <CheckmarkFilled size={40} />
            <p>This invoice has been paid. Thank you!</p>
          </div>
        ) : (
          <Button kind="primary" renderIcon={Currency} onClick={handlePay} disabled={paying}>
            {paying ? 'Redirecting to Stripe...' : `Pay ${format(invoice.total)}`}
          </Button>
        )}
      </Tile>
    </div>
  );
};

export default InvoicePayPage;
